/**
 * FichaPanel — esqueleto de la ficha del elemento (P2RED S1-1 · RED-1=a).
 *
 * Panel derecho permanente del lienzo único P2 (`WorkspaceShell.panelDer`).
 * Acordeón como mecanismo anti-scroll (ratificación 3b, §11.3 CRITERIOS_SHELL):
 * Coordenadas y Propuestas abiertas por defecto; Catastro, Historial de cambios
 * y Contexto plegadas. Esqueleto estructural: las ranuras `secciones` las
 * rellenan S1-3 (acordeón/propuestas/historial/Catastro) y S1-4 (edición de
 * coordenadas). Sin lógica de negocio; único estado interno: qué secciones
 * están desplegadas. Skin DS v2, sin glow (regla dura).
 *
 * @version 1.0.0 (S1-1)
 */

import { useState, type CSSProperties, type FC, type ReactNode } from 'react';
import { colors, typography, spacing } from '@/design-system/v2';
import {
  ETIQUETA_ESTADO_ALBERGUE, motivoAlbergueCompleto, type AptitudAlbergue, type EstadoAlbergue,
} from '@/lib/albergue/aptitudAlbergue';
import { lineasFichaAlbergue, type FichaAlbergue } from '@/lib/albergue/fichaAlbergue';
import { lineaCapacidadAlbergue, type CapacidadAlbergue } from '@/lib/albergue/capacidadAlbergue';
import { cifraRAE } from '@/lib/albergue/fichaAlbergue';
import { ROTULO_TIPO_RTA, type InscripcionRTAFila, type PlazasRegistroFila } from '@/lib/albergue/alojamientosRTA';
import { ATRIBUCION_OPENRTA } from '@/types/openrta';

// ============================================================================
// TYPES
// ============================================================================

/** Identificadores de sección del acordeón (orden canónico §11.3). */
export type FichaSeccionId = 'coordenadas' | 'propuestas' | 'catastro' | 'historial' | 'contexto';

/** Datos mínimos del elemento seleccionado que la ficha muestra en cabecera. */
export interface FichaElemento {
  id: string;
  nombre: string;
  /** Tipología (chip de cabecera). */
  tipo?: string;
  /** Coordenada final UTM30 si el elemento está ubicado. */
  coordenada?: { x: number; y: number } | null;
  /** ALBTIPO: ¿sirve como albergue? (estado + motivo + avisos). */
  albergue?: AptitudAlbergue;
  /** ALBFICHA-a: ficha de albergue del plan (Anexo VI), si el documento la trae rellena. */
  fichaAlbergue?: FichaAlbergue;
  /** ALBCAP-a: capacidad con la regla del plan (DGPC 1989), solo si hay ficha. */
  capacidadAlbergue?: CapacidadAlbergue;
  /**
   * ALBRTA-b2b: plazas del Registro de Turismo de Andalucía (OpenRTA) que casan con el elemento.
   * `cargando`/`fallo` solo llegan para filas cuyo nombre dice alojamiento (DOP-4 b).
   */
  registroRTA?: { estado: 'cargando' } | { estado: 'fallo' } | { estado: 'con_datos'; datos: PlazasRegistroFila };
}

export interface FichaPanelProps {
  /** Elemento seleccionado; sin él la ficha muestra el estado vacío. */
  elemento?: FichaElemento | null;
  /** Ranuras de contenido por sección (las cablean S1-3/S1-4). */
  secciones?: Partial<Record<FichaSeccionId, ReactNode>>;
  /**
   * Pie de ficha fijo (S1-3b): «Confirmar elemento» + navegación entre
   * elementos. Solo se pinta con elemento seleccionado.
   */
  pie?: ReactNode;
}

interface SeccionDef {
  id: FichaSeccionId;
  titulo: string;
  abiertaPorDefecto: boolean;
}

/** Orden y estado inicial del acordeón (ratificación 3b). */
const SECCIONES: readonly SeccionDef[] = [
  { id: 'coordenadas', titulo: 'Coordenadas', abiertaPorDefecto: true },
  { id: 'propuestas', titulo: 'Propuestas', abiertaPorDefecto: true },
  { id: 'catastro', titulo: 'Catastro', abiertaPorDefecto: false },
  { id: 'historial', titulo: 'Historial de cambios', abiertaPorDefecto: false },
  { id: 'contexto', titulo: 'Contexto', abiertaPorDefecto: false },
] as const;

// ============================================================================
// STYLES (DS v2 · sin glow)
// ============================================================================

const raizStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  fontFamily: typography.fontFamily.sans,
  color: colors.fg.secondary,
};

const cabeceraStyle: CSSProperties = {
  padding: `${spacing[3]} ${spacing[4]}`,
  borderBottom: `1px solid ${colors.border.DEFAULT}`,
  background: colors.bg.tertiary,
};

const nombreStyle: CSSProperties = {
  margin: 0,
  fontSize: typography.fontSize.md,
  fontWeight: 600,
  color: colors.fg.primary,
  lineHeight: 1.3,
  wordBreak: 'break-word',
};

const chipTipoStyle: CSSProperties = {
  display: 'inline-block',
  marginTop: spacing[1],
  padding: `1px ${spacing[2]}`,
  fontSize: typography.fontSize.xs,
  fontFamily: typography.fontFamily.mono,
  color: colors.fg.muted,
  border: `1px solid ${colors.border.DEFAULT}`,
  borderRadius: '4px',
};

/** ALBTIPO: línea de albergue bajo el chip de tipología. */
const albergueStyle: CSSProperties = {
  margin: `${spacing[1]} 0 0`,
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
  lineHeight: 1.4,
};

/**
 * ALBFICHA-a: bloque «Ficha de albergue del plan», primero del cuerpo desplazable (la cabecera
 * es fija y no debe crecer con seis líneas más).
 */
const fichaPlanStyle: CSSProperties = {
  margin: 0,
  padding: `${spacing[2]} ${spacing[4]}`,
  borderBottom: `1px solid ${colors.border.DEFAULT}`,
  fontSize: typography.fontSize.xs,
  color: colors.fg.muted,
  lineHeight: 1.4,
};

const fichaPlanListaStyle: CSSProperties = { margin: `${spacing[1]} 0 0`, padding: 0, listStyle: 'none' };

/** ALBCAP-a: línea de capacidad, primera del bloque de la ficha del plan. */
const capacidadStyle: CSSProperties = { margin: `${spacing[1]} 0 0` };

const COLOR_ESTADO_ALBERGUE: Record<EstadoAlbergue, string> = {
  apto: colors.success,
  no_apto: colors.fg.secondary,
  sin_clasificar: colors.fg.muted,
};

const cuerpoStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
};

const seccionCabStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing[2],
  width: '100%',
  padding: `${spacing[2]} ${spacing[4]}`,
  background: 'transparent',
  border: 'none',
  borderBottom: `1px solid ${colors.border.DEFAULT}`,
  color: colors.fg.primary,
  fontFamily: typography.fontFamily.sans,
  fontSize: typography.fontSize.sm,
  fontWeight: 600,
  textAlign: 'left',
  cursor: 'pointer',
};

const seccionCuerpoStyle: CSSProperties = {
  padding: `${spacing[3]} ${spacing[4]}`,
  borderBottom: `1px solid ${colors.border.DEFAULT}`,
  fontSize: typography.fontSize.sm,
};

const placeholderStyle: CSSProperties = {
  margin: 0,
  color: colors.fg.muted,
  fontSize: typography.fontSize.xs,
};

const pieStyle: CSSProperties = {
  flexShrink: 0,
  padding: `${spacing[2]} ${spacing[4]}`,
  borderTop: `1px solid ${colors.border.DEFAULT}`,
  background: colors.bg.tertiary,
};

const vacioStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: spacing[6],
  textAlign: 'center',
  color: colors.fg.muted,
  fontSize: typography.fontSize.sm,
};

const coordStyle: CSSProperties = {
  margin: 0,
  fontFamily: typography.fontFamily.mono,
  fontSize: typography.fontSize.sm,
  color: colors.fg.primary,
};

// ============================================================================
// COMPONENT
// ============================================================================

const plazasRTA = (n: number | null) =>
  n == null ? 'sin plazas en el registro' : `${cifraRAE(n)} ${n === 1 ? 'plaza' : 'plazas'}`;

/** ALBRTA-b2b: «La Zambra · hotel · H/JA/00482 · 16 plazas · a 3 m». */
const lineaInscripcionRTA = (i: InscripcionRTAFila) =>
  [
    i.nombre ?? 'Sin nombre en el registro',
    ROTULO_TIPO_RTA[i.tipo],
    i.codigo,
    plazasRTA(i.plazas),
    i.distanciaM != null ? `a ${cifraRAE(Math.round(i.distanciaM))} m` : null,
  ].filter(Boolean).join(' · ');

/**
 * ALBRTA-b2b: bloque «Plazas del Registro de Turismo de Andalucía publicado (OpenRTA)», aparte
 * de la ficha del plan: el declarado, la capacidad DGPC y el registro nunca se suman.
 */
const RegistroRTABloque: FC<{ registro: NonNullable<FichaElemento['registroRTA']> }> = ({ registro }) => {
  const datos = registro.estado === 'con_datos' ? registro.datos : null;
  return (
    <section
      style={fichaPlanStyle}
      data-testid="ficha-registro-rta"
      data-estado={registro.estado}
      title="Registro de Turismo de Andalucía publicado: no es un censo completo."
    >
      <strong style={{ color: colors.fg.secondary, fontWeight: 600 }}>
        Plazas del Registro de Turismo de Andalucía publicado (OpenRTA)
      </strong>
      {registro.estado === 'cargando' && <div style={capacidadStyle}>Consultando el registro…</div>}
      {registro.estado === 'fallo' && (
        <div style={capacidadStyle}>No se pudo consultar el Registro de Turismo de Andalucía.</div>
      )}
      {datos && datos.inscripciones.length > 0 && (
        <ul style={fichaPlanListaStyle} data-testid="ficha-registro-rta-inscripciones">
          {datos.inscripciones.map((i) => <li key={`${i.codigo}-${i.nombre}`}>{lineaInscripcionRTA(i)}</li>)}
        </ul>
      )}
      {datos && datos.inscripciones.length > 1 && (
        <div style={capacidadStyle} data-testid="ficha-registro-rta-total">
          Total del registro:{' '}
          <strong style={{ color: colors.fg.secondary, fontWeight: 600 }}>{plazasRTA(datos.plazasRegistro)}</strong>
          {' '}({cifraRAE(datos.inscripciones.length)} inscripciones)
        </div>
      )}
      {datos && datos.avisos.length > 0 && (
        <ul style={fichaPlanListaStyle} data-testid="ficha-registro-rta-avisos">
          {datos.avisos.map((a) => <li key={a} style={{ color: colors.warning }}>⚠ {a}</li>)}
        </ul>
      )}
      {datos && (
        <div style={capacidadStyle}>
          {ATRIBUCION_OPENRTA}. No se suman con el aforo declarado ni con la capacidad de albergue.
        </div>
      )}
    </section>
  );
};

/** Metro entero como la tabla/shell; 2 decimales solo con precisión submétrica. */
const formatearCoord = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(2));

/** Chevron inline (evita dependencia de icono); rota 90° al desplegar. */
const Chevron: FC<{ abierta: boolean }> = ({ abierta }) => (
  <svg
    viewBox="0 0 24 24"
    width={12}
    height={12}
    stroke="currentColor"
    strokeWidth={2.5}
    fill="none"
    style={{
      transform: abierta ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      flexShrink: 0,
    }}
    aria-hidden="true"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const FichaPanel: FC<FichaPanelProps> = ({ elemento, secciones, pie }) => {
  const [abiertas, setAbiertas] = useState<Set<FichaSeccionId>>(
    () => new Set(SECCIONES.filter((s) => s.abiertaPorDefecto).map((s) => s.id)),
  );

  const alternar = (id: FichaSeccionId) =>
    setAbiertas((prev) => {
      const sig = new Set(prev);
      if (sig.has(id)) sig.delete(id);
      else sig.add(id);
      return sig;
    });

  if (elemento == null) {
    return (
      <div style={raizStyle} data-testid="ficha-panel">
        <p style={vacioStyle} data-testid="ficha-vacia">
          Selecciona un elemento en el mapa o en la tabla para ver su ficha.
        </p>
      </div>
    );
  }

  const fichaPlan = elemento.fichaAlbergue ? lineasFichaAlbergue(elemento.fichaAlbergue) : [];

  return (
    <div style={raizStyle} data-testid="ficha-panel">
      <header style={cabeceraStyle}>
        <h3 style={nombreStyle}>{elemento.nombre}</h3>
        {elemento.tipo && <span style={chipTipoStyle}>{elemento.tipo}</span>}
        {elemento.albergue && (
          <p
            style={albergueStyle}
            data-testid="ficha-albergue"
            data-estado={elemento.albergue.estado}
            title="¿Sirve como albergue en una emergencia? Se decide por la tipología y el nombre. La capacidad se calcula abajo, en la ficha de albergue del plan."
          >
            Albergue:{' '}
            <strong style={{ color: COLOR_ESTADO_ALBERGUE[elemento.albergue.estado], fontWeight: 600 }}>
              {ETIQUETA_ESTADO_ALBERGUE[elemento.albergue.estado]}
            </strong>
            {' · '}
            {motivoAlbergueCompleto(elemento.albergue)}
          </p>
        )}
      </header>

      <div style={cuerpoStyle}>
        {fichaPlan.length > 0 && (
          <section
            style={fichaPlanStyle}
            data-testid="ficha-albergue-plan"
            title="Datos del Anexo VI del propio plan, tal como los escribe el autor."
          >
            <strong style={{ color: colors.fg.secondary, fontWeight: 600 }}>Ficha de albergue del plan</strong>
            {elemento.capacidadAlbergue && (
              <div
                style={capacidadStyle}
                data-testid="ficha-albergue-capacidad"
                data-limite={elemento.capacidadAlbergue.limite ?? 'no_calculable'}
                title="Regla de la plantilla del PTEL (DGPC 1989): 1 persona por cada 10 m² cerrados, con 1 inodoro y 1 lavabo por cada 50 personas y 1 ducha por cada 100. El área abierta no cuenta."
              >
                Capacidad de albergue (regla del plan):{' '}
                <strong style={{ color: colors.fg.secondary, fontWeight: 600 }}>
                  {lineaCapacidadAlbergue(elemento.capacidadAlbergue)}
                </strong>
                {elemento.capacidadAlbergue.avisos.length > 0 && (
                  <ul style={fichaPlanListaStyle} data-testid="ficha-albergue-capacidad-avisos">
                    {elemento.capacidadAlbergue.avisos.map((a) => (
                      <li key={a} style={{ color: colors.warning }}>⚠ {a}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <ul style={fichaPlanListaStyle}>
              {fichaPlan.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </section>
        )}
        {elemento.registroRTA && <RegistroRTABloque registro={elemento.registroRTA} />}
        {SECCIONES.map(({ id, titulo }) => {
          const abierta = abiertas.has(id);
          return (
            <section key={id} data-testid={`ficha-seccion-${id}`}>
              <button
                type="button"
                style={seccionCabStyle}
                aria-expanded={abierta}
                onClick={() => alternar(id)}
              >
                <Chevron abierta={abierta} />
                {titulo}
              </button>
              {abierta && (
                <div style={seccionCuerpoStyle} data-testid={`ficha-seccion-${id}-cuerpo`}>
                  {secciones?.[id] ??
                    (id === 'coordenadas' && elemento.coordenada != null ? (
                      <p style={coordStyle}>
                        X {formatearCoord(elemento.coordenada.x)} · Y {formatearCoord(elemento.coordenada.y)}
                        <span style={{ color: colors.fg.muted }}> · EPSG:25830</span>
                      </p>
                    ) : (
                      <p style={placeholderStyle}>
                        Disponible en próximas versiones del rediseño.
                      </p>
                    ))}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {pie != null && (
        <footer style={pieStyle} data-testid="ficha-pie">
          {pie}
        </footer>
      )}
    </div>
  );
};

export default FichaPanel;
